// Reimbursement ledger (Round 14 rebuild). A tracker-shaped, TRANSACTION-level
// cost ledger assembled from VERIFIED facts. PURE — no AI, no network, no
// React; unit-tested via node --test.
//
// Core contract (the deterministic template fill):
//   - Unit = transaction, not fact. Verified amounts are deduped per-record by
//     the shipped totaling module (computeCaseTotals: content-key fold +
//     subsumption), then merged ACROSS records on (amount, party, day).
//   - Status drives section placement AND actual-money subtotal inclusion.
//     Only Received → Payments and Paid → Out-of-pocket count toward subtotals.
//     Estimates / quotes / caps / offers / rates never enter the money
//     sections; ambiguous ones land in a Needs-review worklist for the user.
//   - Subtotals route through the SAME integer-cent summation as the case net
//     (sumCents / bucketSubtotalCents) — no parallel math, no drift.
//   - Every row keeps its source(s) for the one-click round-trip; manually
//     entered values are marked distinctly from verbatim-from-source ones.

import type { CaseRecord, Contact, RecordFact } from "../types";
import { parseExtractionConfidence } from "../types";
import { categoryFactKey, type CategoryOverrides, type LedgerDisposition } from "./financialTotals";
import {
  buildLedger,
  normParty,
  pairKey,
  STATUS_DEFS,
  statusTooltip,
  TXN_STATUSES,
  UNTRIAGED_DEF,
  type LedgerOverrides,
  type StatusDef,
  type StatusOverride,
  type TxnStatus,
} from "./ledger";
import { displayFactTitle, formatFactDate } from "./factMeta";

// Re-export the shared status vocabulary + helpers so existing importers
// (ReimbursementSummaryView, exportXlsx) keep importing them from here.
export { TXN_STATUSES, pairKey, STATUS_DEFS, statusTooltip, UNTRIAGED_DEF };
export type { TxnStatus, StatusOverride, StatusDef };

export type RowProvenance = "source" | "manual";
// Visual states so the user sees at a glance what still needs them.
export type RowState =
  | "ok"
  | "needs-status"
  | "possible-dup"
  | "graduation-offered"
  | "gap";

export interface RowSource {
  recordId: string;
  attachmentId: string | null;
}
export type SourceMap = ReadonlyMap<string, RowSource>; // keyed by fact id

export interface LedgerRow {
  key: string; // content key (categoryFactKey) or manual id — stable
  date: string | null; // display
  dateRaw: string | null;
  description: string;
  party: string | null;
  amountCents: number | null; // null ⇒ unvalued (TBD / gap)
  amountEdited: boolean; // user overrode the source amount
  status: TxnStatus | null; // null ⇒ untriaged
  provenance: RowProvenance;
  source: RowSource | null; // primary (best) source
  sources: RowSource[]; // all merged sources
  state: RowState;
  possiblySameAsKey: string | null; // ambiguous duplicate partner
  mergedCount: number; // how many facts/records folded into this row
  // Every mention that grouped into this cost row (grouping-first, Round 16).
  // The unit the user acts on is the COST; mentions are its contributing
  // records — expandable, each viewable in the source panel, each excludable.
  mentions: RowMention[];
}

// One record's mention of a cost row's charge. Superseded = its amount differs
// from the group's counted amount (a cross-stage estimate kept as history but
// excluded from the total).
export interface RowMention {
  key: string; // content key — for the per-mention Exclude (cannot-link) write
  recordId: string;
  date: string | null; // display
  dateRaw: string | null;
  description: string;
  amountCents: number;
  quote: string;
  source: RowSource | null;
  superseded: boolean;
}

export interface LedgerSection {
  rows: LedgerRow[];
  subtotalCents: number;
}

export interface ContactRow {
  key: string;
  type: "email" | "phone";
  value: string; // validated, verbatim from source
  // Already added to the case contact book? (so we hide "Add to contacts").
  // We do NOT guess whose contact it is — the user assigns the party on add.
  promoted: boolean;
  source: RowSource | null;
}

export interface TimelineRow {
  key: string;
  date: string | null;
  description: string;
  party: string | null;
  source: RowSource | null;
}

export interface CompletenessCount {
  needStatus: number; // rows awaiting a Status decision
  possibleDups: number; // ambiguous pairs awaiting merge/keep
  graduationsOffered: number; // TBD items with a proposed resolving fact
  tbdUnvalued: number; // TBD items still missing an amount
  total: number;
}

export interface CollectedContact {
  type: "email" | "phone";
  value: string;
  record_id: string;
  attachment_id: string | null;
  quote: string;
}

export interface ManualTbdItem {
  key: string;
  description: string;
  party: string | null;
  status: TxnStatus | null;
  amountCents: number | null;
  resolvedFactKey: string | null; // set once graduated to a verified amount
}

export interface ReimbursementSummary {
  header: {
    caseName: string;
    firstDate: string | null;
    lastDate: string | null;
    recordCount: number;
  };
  payments: LedgerSection; // Received
  outOfPocket: LedgerSection; // Paid
  tbd: LedgerRow[]; // Need receipt / Outstanding / Accruing + manual
  needsReview: LedgerRow[]; // Estimated / Quote / untriaged — NOT in subtotals
  resolvedNoCost: LedgerRow[]; // Waived / no cost — documented, amount kept, $0 to user
  // Documented — uncosted: confirmed items with NO price (deliberately absent
  // from the estimate). Distinct from Accruing (a running meter): these are a
  // PRESENT GAP whose absence understates the headline total. Their own callout.
  documentedUncosted: LedgerRow[];
  contacts: ContactRow[];
  timeline: TimelineRow[];
  completeness: CompletenessCount;
  // Graduation offers: a manual TBD item + the verified fact-key that may
  // resolve it. The UI shows "Link / Keep separate"; never auto-linked.
  graduationOffers: Array<{ tbdKey: string; factKey: string; description: string }>;
}

export interface SummaryInputs {
  caseName: string;
  records: CaseRecord[];
  facts: RecordFact[];
  // Bucket overrides feed computeCaseTotals (existing "confirmed" gate).
  bucketOverrides: CategoryOverrides;
  // Per-transaction status + amount corrections (content-keyed).
  statusOverrides: ReadonlyMap<string, StatusOverride>;
  // Confirmed merge/keep-separate for ambiguous pairs (key: pairKey).
  mergeLinks: ReadonlyMap<string, "merge" | "separate">;
  manualTbd: ManualTbdItem[];
  sources: SourceMap;
  collectedContacts: CollectedContact[];
  manualContacts: Contact[];
  todayIso: string;
}

// ---- helpers --------------------------------------------------------------

function sourceFor(fact: RecordFact, sources: SourceMap): RowSource {
  return sources.get(fact.id) ?? { recordId: fact.record_id, attachmentId: null };
}

// ---- main -----------------------------------------------------------------

export function buildReimbursementSummary(input: SummaryInputs): ReimbursementSummary {
  const {
    caseName,
    records,
    facts,
    bucketOverrides,
    statusOverrides,
    mergeLinks,
    manualTbd,
    sources,
    collectedContacts,
    manualContacts,
    todayIso: _todayIso,
  } = input;

  const recordById = new Map(records.map((r) => [r.id, r]));
  const dates = records.map((r) => r.record_date).filter((d): d is string => !!d).sort();

  // 1-3) THE shared transaction layer (cross-record merge + effective
  //      disposition + amount override + net). The Summary and the Overview
  //      net are two presentations of this one result — equal by construction.
  const ledger = buildLedger(facts, { bucketOverrides, statusOverrides, mergeLinks });
  const txns = ledger.transactions;

  // Map each transaction to a display row (description/source/state added here;
  // the financial fields come straight from the shared layer).
  const rows: LedgerRow[] = txns.map((t) => {
    const repFact = t.items[0]!.fact;
    return {
      key: t.key,
      date: formatFactDate(t.dateRaw),
      dateRaw: t.dateRaw,
      description: displayFactTitle(repFact),
      party: t.party,
      amountCents: t.amountCents,
      amountEdited: t.amountEdited,
      status: t.status,
      provenance: "source",
      source: sourceFor(repFact, sources),
      sources: t.items.map((i) => sourceFor(i.fact, sources)),
      state: t.possiblySameAsKey
        ? "possible-dup"
        : t.status === null
          ? "needs-status"
          : "ok",
      possiblySameAsKey: t.possiblySameAsKey,
      mergedCount: t.mergedCount,
      mentions: t.items.map((i) => ({
        key: i.key,
        recordId: i.fact.record_id,
        date: formatFactDate(i.fact.date),
        dateRaw: i.fact.date,
        description: displayFactTitle(i.fact),
        amountCents: i.cents,
        quote: i.fact.verbatim_quote,
        source: sourceFor(i.fact, sources),
        // Superseded = differs from the group's counted amount (cross-stage
        // estimate kept as history, excluded from the total).
        superseded: t.amountCents != null && i.cents !== t.amountCents,
      })),
    };
  });

  // 4) Section placement by status.
  const payments: LedgerRow[] = [];
  const outOfPocket: LedgerRow[] = [];
  const tbd: LedgerRow[] = [];
  const needsReview: LedgerRow[] = [];
  const resolvedNoCost: LedgerRow[] = [];
  const documentedUncosted: LedgerRow[] = [];
  for (const r of rows) {
    switch (r.status) {
      case "Received":
        payments.push(r);
        break;
      case "Paid":
        outOfPocket.push(r);
        break;
      case "Need receipt":
      case "Outstanding":
      case "Accruing":
        tbd.push({ ...r, state: r.amountCents === null ? "gap" : r.state });
        break;
      case "Documented — uncosted":
        // A present gap, not a forward liability — its own callout, never TBD.
        documentedUncosted.push({ ...r, state: "ok" });
        break;
      case "Waived / no cost":
        resolvedNoCost.push({ ...r, state: "ok" });
        break;
      default: // Estimated | Quote | Disputed | null
        needsReview.push(r);
    }
  }

  // 5) Manual TBD items + graduation. A resolved (graduated) manual item is
  //    rendered in its money section, NOT in TBD, and never duplicates the
  //    resolving txn (the txn it points to is suppressed from its section).
  const graduationOffers: ReimbursementSummary["graduationOffers"] = [];
  const resolvedFactKeys = new Set<string>();
  for (const item of manualTbd) {
    if (item.resolvedFactKey) {
      resolvedFactKeys.add(item.resolvedFactKey);
      // Graduated: place by its (override) status; default Paid.
      const ov = statusOverrides.get(item.key);
      const status = ov?.status ?? item.status ?? "Paid";
      const resolvingTxn = txns.find((t) => t.key === item.resolvedFactKey);
      const repFact = resolvingTxn?.items[0]?.fact ?? null;
      // The resolving transaction supplies the missing amount when the TBD
      // placeholder had none — that IS the graduation.
      const amountCents =
        ov?.amountCentsOverride ?? item.amountCents ?? resolvingTxn?.amountCents ?? null;
      const row: LedgerRow = {
        key: item.key,
        date: repFact ? formatFactDate(repFact.date) : null,
        dateRaw: repFact?.date ?? null,
        description: item.description,
        party: item.party,
        amountCents,
        amountEdited: ov?.amountCentsOverride != null,
        status,
        provenance: repFact ? "source" : "manual",
        source: repFact ? sourceFor(repFact, sources) : null,
        sources: repFact ? [sourceFor(repFact, sources)] : [],
        state: "ok",
        possiblySameAsKey: null,
        mergedCount: 1,
        mentions: [],
      };
      if (status === "Received") payments.push(row);
      else outOfPocket.push(row);
    } else {
      // Unresolved manual item. Status drives placement, same as transactions:
      // a "Documented — uncosted" manual entry (the classic case — a confirmed
      // item with no price) belongs in the callout, NOT in TBD. The inline
      // status dropdown writes a content-keyed override, so honor it here (as
      // the graduated branch already does) — otherwise the dropdown wouldn't
      // move a manual item.
      const manualStatus = statusOverrides.get(item.key)?.status ?? item.status;
      const manualRow: LedgerRow = {
        key: item.key,
        date: null,
        dateRaw: null,
        description: item.description,
        party: item.party,
        amountCents: item.amountCents,
        amountEdited: item.amountCents != null,
        status: manualStatus,
        provenance: "manual",
        source: null,
        sources: [],
        state: item.amountCents === null ? "gap" : "ok",
        possiblySameAsKey: null,
        mergedCount: 1,
        mentions: [],
      };
      if (manualStatus === "Documented — uncosted") documentedUncosted.push({ ...manualRow, state: "ok" });
      else tbd.push(manualRow);
      // Offer a graduation link to any verified cost txn with the same party
      // (deterministic where provable; surfaced, never auto-linked).
      const np = normParty(item.party);
      if (np) {
        for (const t of txns) {
          if (resolvedFactKeys.has(t.key)) continue;
          if (normParty(t.party) === np) {
            const pk = pairKey(item.key, t.key);
            if (mergeLinks.get(pk) === "separate") continue;
            graduationOffers.push({ tbdKey: item.key, factKey: t.key, description: t.items[0]!.fact.summary });
            break; // one offer per item keeps the worklist quiet
          }
        }
      }
    }
  }

  // A resolving txn graduated into a manual item must not ALSO appear on its
  // own in a money/needs-review section.
  const drop = (arr: LedgerRow[]) => arr.filter((r) => !resolvedFactKeys.has(r.key));
  const paymentsRows = drop(payments);
  const outRows = drop(outOfPocket);
  const needsReviewRows = drop(needsReview);
  const tbdRows = drop(tbd);
  const resolvedNoCostRows = drop(resolvedNoCost);
  const documentedUncostedRows = drop(documentedUncosted);

  // Completeness — what stands between the user and a finished ledger.
  const needStatus = needsReviewRows.filter((r) => r.status === null).length;
  const possibleDups = new Set(
    [...paymentsRows, ...outRows, ...needsReviewRows]
      .filter((r) => r.possiblySameAsKey)
      .map((r) => pairKey(r.key, r.possiblySameAsKey!)),
  ).size;
  const tbdUnvalued = tbdRows.filter((r) => r.amountCents === null).length;
  const completeness: CompletenessCount = {
    needStatus,
    possibleDups,
    graduationsOffered: graduationOffers.length,
    tbdUnvalued,
    total: needStatus + possibleDups + graduationOffers.length + tbdUnvalued,
  };

  return {
    header: {
      caseName,
      firstDate: dates[0] ?? null,
      lastDate: dates[dates.length - 1] ?? null,
      recordCount: records.length,
    },
    // Subtotals are the canonical net values from the shared layer, so the
    // Summary and the Overview net are equal by construction (Received →
    // recovery, Paid → cost).
    payments: { rows: paymentsRows, subtotalCents: ledger.net.recoveryCents },
    outOfPocket: { rows: outRows, subtotalCents: ledger.net.costCents },
    tbd: tbdRows,
    needsReview: needsReviewRows,
    resolvedNoCost: resolvedNoCostRows,
    documentedUncosted: documentedUncostedRows,
    contacts: buildContacts(collectedContacts, manualContacts),
    timeline: buildTimeline(facts, sources),
    completeness,
    graduationOffers,
  };
}

function phoneDigits(s: string): string {
  const d = s.replace(/\D/g, "");
  return d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
}
function contactDedupValue(type: "email" | "phone", value: string): string {
  return type === "email" ? value.toLowerCase() : phoneDigits(value);
}

function buildContacts(collected: CollectedContact[], manual: Contact[]): ContactRow[] {
  const manualValues = new Set<string>();
  for (const mc of manual) {
    if (mc.email) manualValues.add(contactDedupValue("email", mc.email));
    if (mc.phone) manualValues.add(contactDedupValue("phone", mc.phone));
  }
  return collected.map((cc) => ({
    key: `contact:${cc.type}:${contactDedupValue(cc.type, cc.value)}`,
    type: cc.type,
    value: cc.value,
    promoted: manualValues.has(contactDedupValue(cc.type, cc.value)),
    source: { recordId: cc.record_id, attachmentId: cc.attachment_id },
  }));
}

function buildTimeline(facts: RecordFact[], sources: SourceMap): TimelineRow[] {
  return facts
    .filter((f) => !!f.date)
    .slice()
    .sort((a, b) => (a.date! < b.date! ? -1 : a.date! > b.date! ? 1 : 0))
    .map((f) => ({
      key: `tl:${f.id}`,
      date: formatFactDate(f.date),
      description: displayFactTitle(f),
      party: f.party,
      source: sourceFor(f, sources),
    }));
}

// The exact navigation argument a row click produces — pure so the
// round-trip's "click → correct source" is unit-testable without a DOM.
export function navTargetForRow(row: {
  source: RowSource | null;
}): { recordId: string; attachmentId: string | null } | null {
  if (!row.source) return null;
  return { recordId: row.source.recordId, attachmentId: row.source.attachmentId };
}

// Overlay the user's session edits on top of freshly-fetched server state so a
// remount (after viewing a source) can't drop a just-made change while the
// write round-trips. The cached edit is the user's latest intent → it wins on
// conflict. Pure + unit-tested.
export function overlayMap<V>(
  server: ReadonlyMap<string, V>,
  cached: ReadonlyMap<string, V>,
): Map<string, V> {
  return new Map([...server, ...cached]);
}

export function overlayTbd(
  server: ManualTbdItem[],
  cached: ManualTbdItem[],
): ManualTbdItem[] {
  const byKey = new Map(server.map((i) => [i.key, i]));
  for (const i of cached) byKey.set(i.key, i); // user's latest wins
  return [...byKey.values()];
}

// ---------------------------------------------------------------------------
// Stale-override detection (audit H1). Overrides are content-keyed on
// record_id|amountCents|quoteHead. Re-extraction (which reinserts record_facts
// with new ids) leaves the override stores untouched, so a reworded quote or a
// shifted amount changes the recomputed key and the override silently stops
// applying — the row reverts to untriaged, an edit/merge/graduation orphans.
// buildLedger never reads these (it looks up by CURRENT transaction keys), so
// they can't count toward or suppress the net — they're inert, not corrupting.
// The defect is invisibility; this detector surfaces them for the user to
// re-link or dismiss. NO auto-delete, NO auto-reapply.

export interface StaleOverride {
  kind: "status" | "merge" | "graduation";
  // Identity of the stale override: the fact_key (status), the pairKey (merge),
  // or the manual TBD item key (graduation).
  key: string;
  // Self-describing detail parsed from the content key (the fact is gone, but
  // the key still records what it was).
  recordId: string | null;
  amountCents: number | null;
  quoteHead: string | null;
  // Remembered decision (for one-click re-link onto a current row).
  status: TxnStatus | null;
  bucket: LedgerDisposition | null;
  amountOverride: number | null;
  description: string | null; // graduation: the manual TBD description
}

export interface StaleDetectInput {
  bucketOverrides: CategoryOverrides;
  statusOverrides: ReadonlyMap<string, StatusOverride>;
  mergeLinks: ReadonlyMap<string, "merge" | "separate">;
  manualTbd: ManualTbdItem[];
}

function parseFactKey(key: string): {
  recordId: string | null;
  amountCents: number | null;
  quoteHead: string | null;
} {
  const parts = key.split("|");
  if (parts.length < 3) return { recordId: null, amountCents: null, quoteHead: null };
  const cents = Number(parts[1]);
  return {
    recordId: parts[0] ?? null,
    amountCents: Number.isFinite(cents) ? cents : null,
    quoteHead: parts.slice(2).join("|"),
  };
}

export function detectStaleOverrides(
  facts: RecordFact[],
  input: StaleDetectInput,
): StaleOverride[] {
  const { bucketOverrides, statusOverrides, mergeLinks, manualTbd } = input;
  const validKeys = new Set(
    facts.filter((f) => f.fact_type === "amount" && f.verified).map(categoryFactKey),
  );
  const out: StaleOverride[] = [];

  // Status / bucket / amount overrides — keyed by a single content key.
  const overrideKeys = new Set<string>([
    ...statusOverrides.keys(),
    ...bucketOverrides.keys(),
  ]);
  for (const key of overrideKeys) {
    if (validKeys.has(key)) continue;
    const so = statusOverrides.get(key);
    out.push({
      kind: "status",
      key,
      ...parseFactKey(key),
      status: so?.status ?? null,
      bucket: bucketOverrides.get(key) ?? null,
      amountOverride: so?.amountCentsOverride ?? null,
      description: null,
    });
  }

  // Confirmed merges: stale only when a "merge" decision references a key that
  // no longer exists (a "separate" decision is inert and never flagged).
  for (const [pk, decision] of mergeLinks) {
    if (decision !== "merge") continue;
    const sides = pk.split("::");
    if (sides.length === 2 && validKeys.has(sides[0]!) && validKeys.has(sides[1]!)) continue;
    const missing = sides.find((s) => !validKeys.has(s)) ?? pk;
    out.push({
      kind: "merge",
      key: pk,
      ...parseFactKey(missing),
      status: null,
      bucket: null,
      amountOverride: null,
      description: null,
    });
  }

  // Graduations: a manual TBD item resolved to a fact whose key no longer exists.
  for (const item of manualTbd) {
    if (!item.resolvedFactKey || validKeys.has(item.resolvedFactKey)) continue;
    out.push({
      kind: "graduation",
      key: item.key,
      ...parseFactKey(item.resolvedFactKey),
      status: item.status,
      bucket: null,
      amountOverride: item.amountCents,
      description: item.description,
    });
  }

  return out;
}

// The status to re-apply when re-linking a stale override onto a current row:
// the remembered status, or a bucket choice mapped to its money side.
export function relinkStatus(stale: StaleOverride): TxnStatus | null {
  if (stale.status) return stale.status;
  if (stale.bucket === "recovery") return "Received";
  if (stale.bucket === "cost") return "Paid";
  return null;
}

// ---------------------------------------------------------------------------
// Load controller (audit M4 / H3). Pure merge of the Financials view's five
// independently-settled store fetches + the session edit cache into the view's
// state — extracted so the load-failure severity split and the cache overlay
// (where the remount-loss + CORS bugs lived) are unit-tested without a DOM.

export interface Settled<T> {
  ok: boolean;
  value?: T;
}
interface OverrideRow {
  fact_key: string;
  bucket: string;
  status?: string | null;
  amount_cents_override?: number | null;
}
interface MergeLinkRow {
  pair_key: string;
  decision: string;
}
interface TbdRow {
  key: string;
  description: string;
  party: string | null;
  status?: string | null;
  amount_cents: number | null;
  resolved_fact_key: string | null;
}
interface SourcesResp {
  sources: Array<{ fact_id: string; record_id: string; attachment_id: string | null }>;
  contacts: CollectedContact[];
}

export interface LedgerLoadResults {
  facts: Settled<RecordFact[]>;
  overrides: Settled<OverrideRow[]>;
  links: Settled<MergeLinkRow[]>;
  tbd: Settled<TbdRow[]>;
  sources: Settled<SourcesResp>;
}

export interface LedgerEdits {
  bucketOverrides: Map<string, LedgerDisposition>;
  statusOverrides: Map<string, StatusOverride>;
  mergeLinks: Map<string, "merge" | "separate">;
  manualTbd: ManualTbdItem[];
}

export interface LoadedLedgerState extends LedgerEdits {
  factsFailed: boolean; // can't build the ledger at all
  savedDataFailed: boolean; // facts ok, but a saved-decisions/sources store failed
  facts: RecordFact[];
  sources: Map<string, RowSource>;
  collected: CollectedContact[];
}

export function buildLoadedLedgerState(
  r: LedgerLoadResults,
  cached: LedgerEdits | undefined,
): LoadedLedgerState {
  const empty: LoadedLedgerState = {
    factsFailed: false,
    savedDataFailed: false,
    facts: [],
    bucketOverrides: new Map(),
    statusOverrides: new Map(),
    mergeLinks: new Map(),
    manualTbd: [],
    sources: new Map(),
    collected: [],
  };
  if (!r.facts.ok) return { ...empty, factsFailed: true };

  // A failed saved-decisions/sources store is flagged (NOT treated as empty),
  // so an untriaged-looking ledger is never mistaken for the truth.
  const savedDataFailed = !r.overrides.ok || !r.links.ok || !r.tbd.ok || !r.sources.ok;
  const ov = r.overrides.value ?? [];
  const links = r.links.value ?? [];
  const tbd = r.tbd.value ?? [];
  const ss = r.sources.value ?? { sources: [], contacts: [] };

  const serverBucket = new Map<string, LedgerDisposition>(
    ov.map((o) => [o.fact_key, o.bucket as LedgerDisposition]),
  );
  const serverStatus = new Map<string, StatusOverride>(
    ov
      .filter((o) => o.status != null || o.amount_cents_override != null)
      .map((o) => [o.fact_key, { status: (o.status ?? null) as TxnStatus | null, amountCentsOverride: o.amount_cents_override ?? null }]),
  );
  const serverMerge = new Map<string, "merge" | "separate">(
    links.map((l) => [l.pair_key, l.decision as "merge" | "separate"]),
  );
  const serverTbd: ManualTbdItem[] = tbd.map((t) => ({
    key: t.key,
    description: t.description,
    party: t.party,
    status: (t.status ?? null) as TxnStatus | null,
    amountCents: t.amount_cents,
    resolvedFactKey: t.resolved_fact_key,
  }));

  return {
    factsFailed: false,
    savedDataFailed,
    facts: r.facts.value ?? [],
    // Overlay the session edits (cache wins) so a remount after "view source"
    // can't drop a just-made change.
    bucketOverrides: cached ? overlayMap(serverBucket, cached.bucketOverrides) : serverBucket,
    statusOverrides: cached ? overlayMap(serverStatus, cached.statusOverrides) : serverStatus,
    mergeLinks: cached ? overlayMap(serverMerge, cached.mergeLinks) : serverMerge,
    manualTbd: cached ? overlayTbd(serverTbd, cached.manualTbd) : serverTbd,
    sources: new Map(ss.sources.map((s) => [s.fact_id, { recordId: s.record_id, attachmentId: s.attachment_id }])),
    collected: ss.contacts,
  };
}
